import { Navigate, useLocation } from "react-router-dom";

import { useAppUser, AppUserState } from "./AppUserProvider";
import "./Auth.css";
import BrandWordmark from "../components/BrandWordmark";

/**
 * Gate for the authenticated area.
 *
 * A signed-out visitor is sent to the Clerk sign-in experience. A signed-in one
 * whose OMJ VISION account is missing or disabled gets an explanation rather
 * than the app — the backend would refuse every request anyway, so showing the
 * dashboard shell would only produce a wall of failed panels.
 */
function RequireAuth({ children, permission, role }) {
  const { state, appUser, hasPermission, hasRole } = useAppUser();
  const location = useLocation();

  if (state === AppUserState.LOADING) {
    return <AuthSplash title="Loading your account…" />;
  }

  if (state === AppUserState.SIGNED_OUT) {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }

  if (state === AppUserState.NOT_PROVISIONED) {
    return (
      <AuthNotice
        title="Account not set up"
        body="Your sign-in worked, but this account has not been added to OMJ VISION yet. Ask an administrator to set it up."
      />
    );
  }

  if (state === AppUserState.DISABLED) {
    return (
      <AuthNotice
        title="Account disabled"
        body="This OMJ VISION account has been disabled. Contact an administrator if you think that is a mistake."
      />
    );
  }

  if (state === AppUserState.ERROR || !appUser) {
    return (
      <AuthNotice
        title="Could not load your account"
        body="OMJ VISION could not reach its backend. Try again in a moment."
      />
    );
  }

  if (role && !hasRole(role)) {
    return <AuthNotice title="Not available" body="You do not have access to this page." />;
  }

  if (permission && !hasPermission(permission)) {
    return <AuthNotice title="Not available" body="You do not have access to this page." />;
  }

  return children;
}

export function AuthSplash({ title }) {
  return (
    <main className="auth-shell">
      <div className="auth-card auth-card--slim">
        <span className="auth-spinner" aria-hidden="true" />
        <p>{title}</p>
      </div>
    </main>
  );
}

export function AuthNotice({ title, body }) {
  return (
    <main className="auth-shell">
      <div className="auth-card auth-card--slim">
        <BrandWordmark size="sm" className="auth-kicker" />
        <h1>{title}</h1>
        <p>{body}</p>
      </div>
    </main>
  );
}

export default RequireAuth;
